import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import CustomLoading from '../../../util_components/custom_loading';
import LoadFailed from '../shared/load_failed';
import StatusPill from '../shared/status_pill';
import TpDialog from '../shared/tp_dialog';
import CreateNotice from './month_run/create_notice';
import RunConfirm from './month_run/run_confirm';
import SelectionBar from './month_run/selection_bar';
import { FilterBar, LabeledSegmented } from '../shared/controls';
import { ReadinessPager, TeacherSearch } from './readiness_controls';
import useReadinessList from './use_readiness_list';
import useRunCreate from './month_run/use_run_create';
import {
  CARRY_FORWARD_TEXT,
  CLOSE_DISABLED_TEXT,
  READINESS_LIST_LOAD_FAILED_TEXT,
  close_response_view,
  close_result_tone
} from './adapters';
import {
  run_confirm_text,
  selection_changed_text,
  selection_reask_count,
  teacher_months
} from './month_run/adapters';
import { discard_guard_text } from '../shared/dialog_logic';
import PayTimeWarningStep from '../../../util_components/pay_time_warning';
import {
  DETAIL_EFFECT,
  read_pay_time_warning
} from '../../../util_components/pay_time_warning/pay_time_warning_rules';
import {
  DEFAULT_READINESS_STATE,
  READINESS_STATES,
  close_form_error,
  needs_override_reason,
  retry_close_uuids,
  selectable_close_uuids
} from './params';
import {
  RUN_FILTER_STATES,
  RUN_KIND_CLOSE,
  filter_selection,
  list_selection,
  retry_of_run
} from './month_run/params';

/*
 * Submitting starts a server-side run (POST /month/runs) and hands it to the tab's run panel; the
 * teachers are closed one by one on the server, so nothing here waits on a long request.
 */

const STATE_OPTIONS = READINESS_STATES.map((s) => ({
  value: s,
  label: s.charAt(0) + s.slice(1).toLowerCase()
}));

// A selection keyed by teacher uuid, in the order picked; each entry is the row as it was picked.
const pick = (rows_by_uuid, uuids) =>
  uuids.reduce((acc, u) => (rows_by_uuid[u] ? { ...acc, [u]: rows_by_uuid[u] } : acc), {});

const by_uuid = (rows) => rows.reduce((acc, r) => ({ ...acc, [r.teacher_uuid]: r }), {});

const blocker_pill = (b) => (
  <StatusPill tone={b.tone}>{b.overridable ? 'Overridable' : 'Blocks close'}</StatusPill>
);

const CloseMonthModal = ({
  show,
  pay_month,
  month_label,
  seed = null,
  on_close,
  on_started,
  on_show_run,
  on_error
}) => {
  const [state, setState] = useState(DEFAULT_READINESS_STATE);
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState({});
  // "Select all N matching": {state, q, count}. Dropped as soon as the filters it names change.
  const [match_all, setMatchAll] = useState(null);
  const [reason, setReason] = useState('');
  const [override_reason, setOverrideReason] = useState('');
  const [touched, setTouched] = useState(false);
  // The pre-check refusal (nothing was created), shown with its per-teacher blockers.
  const [result, setResult] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [refusal, setRefusal] = useState(null);
  const [disabled_text, setDisabledText] = useState('');
  // the server's own REASON_TOO_LONG message, shown where the reason is typed.
  const [reason_server_error, setReasonServerError] = useState('');
  const [force_override, setForceOverride] = useState(false);
  // A-52: e.g. MONTH_NOT_ENDED; the step replaces the form until acknowledged or changed.
  const [pay_warning, setPayWarning] = useState(null);
  const submitted_ref = useRef({});
  // When the form swaps for the pre-check result, focus follows it.
  const outcome_ref = useRef(null);
  useEffect(() => {
    if (result && outcome_ref.current) outcome_ref.current.focus({ preventScroll: true });
  }, [result]);

  const { list, listed_query, search_error, loading, loading_more, failed, load_more, retry } =
    useReadinessList({
      show,
      pay_month,
      state,
      query,
      on_error,
      error_text: 'Failed to load the teachers ready to close.'
    });
  const selected = Object.keys(picked);

  // A retry run's teachers and reason arrive once per "Retry the teachers that failed".
  const seed_token = seed ? seed.token : 0;
  useLayoutEffect(() => {
    if (!show || !seed) return;
    setPicked(by_uuid(seed.rows));
    setReason(seed.reason || '');
    setMatchAll(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, seed_token]);

  useEffect(() => {
    setMatchAll(null);
    setConfirm(null);
  }, [state, query]);

  // Rows (and the month headline that came with them) only for a search that was sent and answered.
  const list_ready = !loading && !search_error && (failed === '' || failed === 'more');
  // the list's "Try again" unmounts once pressed; focus waits on the search above the list.
  const search_ref = useRef(null);
  const retry_list = () => {
    if (search_ref.current) search_ref.current.focus({ preventScroll: true });
    retry();
  };
  // "Add an override reason" unmounts with the result; focus goes to the textarea it asked for.
  const override_ref = useRef(null);
  const focus_override = useRef(false);
  useLayoutEffect(() => {
    if (!focus_override.current || !override_ref.current) return;
    focus_override.current = false;
    override_ref.current.focus({ preventScroll: true });
  }, [result]);

  const reset = () => {
    setForceOverride(false);
    setPicked({});
    setMatchAll(null);
    setQuery('');
    setReason('');
    setOverrideReason('');
    setTouched(false);
    setResult(null);
    setConfirm(null);
    setRefusal(null);
    setDisabledText('');
    setReasonServerError('');
    setPayWarning(null);
  };

  const create = useRunCreate({
    pay_month,
    on_started: (run_uuid, run) => {
      reset();
      on_started(run_uuid, run);
    },
    on_precheck: (precheck) => {
      setPayWarning(null);
      setConfirm(null);
      setResult(close_response_view(precheck));
    },
    on_refused: (failure, e) => {
      const warning = read_pay_time_warning(e);
      if (warning) {
        setConfirm(null);
        setPayWarning(warning);
        return;
      }
      setPayWarning(null);
      if (failure.kind === 'selection_changed' && match_all) {
        // The confirm is asked again with the count the list matches now.
        const again = selection_reask_count(failure);
        if (again) {
          setMatchAll({ ...match_all, count: again });
          setConfirm({ note: selection_changed_text(failure, match_all.count) });
          return;
        }
        // Nothing one run can take: say why, with no confirm to press.
        setMatchAll(null);
        setConfirm(null);
        setRefusal({ message: selection_changed_text(failure, match_all.count), run_uuid: '' });
        return;
      }
      setConfirm(null);
      if (failure.kind === 'disabled') setDisabledText(CLOSE_DISABLED_TEXT);
      else if (failure.kind === 'reason_too_long') {
        setReasonServerError(failure.message || 'The reason is too long.');
      } else if (failure.kind === 'already_active' || failure.kind === 'idempotency_mismatch') {
        setRefusal({ message: failure.message, run_uuid: failure.run_uuid });
      } else on_error(e, 'The month close was not started. Nothing was written.');
    }
  });

  const close = () => {
    create.reset();
    reset();
    on_close();
  };

  const requires_override =
    force_override ||
    (match_all
      ? match_all.state === 'BLOCKED'
      : needs_override_reason(Object.values(picked), selected));
  const form_error = close_form_error({
    selected_uuids: selected,
    filter_count: match_all ? match_all.count : 0,
    reason,
    override_reason,
    requires_override
  });

  const toggle = (row) =>
    setPicked((p) => {
      if (!p[row.teacher_uuid]) return { ...p, [row.teacher_uuid]: row };
      const { [row.teacher_uuid]: _drop, ...rest } = p;
      return rest;
    });

  const shown_selectable = selectable_close_uuids(list.rows);
  const shown_picked = shown_selectable.filter((u) => picked[u]).length;

  // The header box adds the rows on screen (or takes them off); picks under another search stay.
  const toggle_shown = () =>
    setPicked((p) => {
      if (shown_selectable.length && shown_picked === shown_selectable.length) {
        return Object.keys(p).reduce(
          (acc, u) => (shown_selectable.indexOf(u) === -1 ? { ...acc, [u]: p[u] } : acc),
          {}
        );
      }
      return { ...p, ...pick(by_uuid(list.rows), shown_selectable) };
    });

  const matching =
    list_ready && list.has_total && RUN_FILTER_STATES[RUN_KIND_CLOSE].indexOf(state) !== -1
      ? list.total_count
      : null;

  // ack: the pay-time acknowledgement from the warning step; null on the first send
  const send = (ack = null) => {
    submitted_ref.current = picked;
    setRefusal(null);
    setReasonServerError('');
    create.send({
      pay_time_ack: ack,
      kind: RUN_KIND_CLOSE,
      reason,
      override_reason: requires_override ? override_reason : '',
      retry_of_run_uuid: retry_of_run(seed, selected, match_all),
      selection: match_all
        ? filter_selection(match_all.state, match_all.q, match_all.count)
        : list_selection(selected)
    });
  };

  // The form is checked again on the confirm: a reason cleared meanwhile sends nothing.
  const confirm_send = () => {
    setTouched(true);
    if (form_error || create.busy) {
      setConfirm(null);
      return;
    }
    send();
  };

  const submit = () => {
    setTouched(true);
    if (form_error || create.busy) return;
    if (match_all) {
      setConfirm({ note: '' });
      return;
    }
    send();
  };

  // The pre-check is often the first thing that asks for the override reason (the default READY filter
  // never renders the textarea), so the refusal returns to the form with the selection and both reasons.
  const add_override_reason = () => {
    if (!match_all) {
      const submitted = submitted_ref.current;
      setPicked(
        pick(submitted, retry_close_uuids(Object.keys(submitted), result ? result.results : []))
      );
    }
    setForceOverride(true);
    setTouched(false);
    focus_override.current = true;
    setResult(null);
  };

  const render_row = (row) => (
    <div key={row.key} className="tp-na-row">
      <label className="tp-na-row-main">
        <input
          type="checkbox"
          checked={!!match_all || !!picked[row.teacher_uuid]}
          onChange={() => (confirm ? undefined : toggle(row))}
          disabled={!!match_all || !row.selectable || !!confirm}
        />
        <span>
          <b>{row.teacher_name}</b>
          {row.net_text ? (
            <>
              {' · '}
              <span className={row.net_tone}>{row.net_text}</span>
            </>
          ) : null}
          {row.sessions_text ? ` · ${row.sessions_text}` : ''}
        </span>
      </label>
      {row.blockers.map((b) => (
        <div key={b.key} className="tp-na-blocker">
          {blocker_pill(b)} {b.line}
        </div>
      ))}
    </div>
  );

  const render_result_row = (r) => (
    <div key={r.key} className="tp-na-row">
      <div>
        <StatusPill tone={r.outcome_tone}>{r.outcome_label}</StatusPill> <b>{r.teacher_name}</b>
        {r.net_text ? ` · ${r.net_text}` : ''}
      </div>
      {/* The override headline points at these pills. */}
      {r.blockers.map((b) => (
        <div key={b.key} className="tp-na-blocker">
          {blocker_pill(b)} {b.line}
        </div>
      ))}
      {r.grant_mismatch.map((g) => (
        <div key={g.key} className="tp-na-blocker">
          {g.line}
          {g.session_href ? (
            <>
              {' · '}
              <a
                className="tp-link"
                href={g.session_href}
                target="_blank"
                rel="noopener noreferrer"
              >
                {/* the date-time never breaks at its hyphens; the name wraps on its own */}
                {g.session_link_text.split(' · ').map((part, i) => (
                  <React.Fragment key={part}>
                    {i ? ' · ' : ''}
                    <span className={i ? undefined : 'tp-nowrap'}>{part}</span>
                  </React.Fragment>
                ))}
              </a>
            </>
          ) : null}{' '}
          — {g.how_to_fix}
        </div>
      ))}
      {r.grant_mismatch_unnamed_text ? (
        <div className="tp-na-blocker">{r.grant_mismatch_unnamed_text}</div>
      ) : null}
    </div>
  );

  // A typed reason plus a whole selection is too expensive to lose to a stray Escape or a mis-click.
  const guard_text = result ? '' : discard_guard_text(reason, override_reason);
  // the switch is known from the list read, before any reason is typed.
  const switch_text = disabled_text || (list.close_enabled ? '' : CLOSE_DISABLED_TEXT);
  const count = match_all ? match_all.count : selected.length;
  // Nothing picked: the primary is off and names no count ("Close 0 …" read as an action).
  const submit_off = create.busy || !!switch_text || count === 0;

  return (
    <TpDialog
      show={show}
      on_close={close}
      busy={create.busy}
      guard_text={guard_text}
      title={`Close ${month_label}`}
      size="lg"
    >
      <div className="tp-modal tp-na-dlg">
        {switch_text ? (
          <div className="tp-banner tp-banner-warn" role="alert">
            {switch_text}
          </div>
        ) : null}

        {pay_warning && !result ? (
          <PayTimeWarningStep
            warning={pay_warning}
            detail={DETAIL_EFFECT}
            busy={create.busy}
            changeLabel="Back"
            actions="tp"
            onAcknowledge={(ack) => send(ack)}
            onChange={() => setPayWarning(null)}
          />
        ) : null}
        {pay_warning && !result ? null : result ? (
          <section aria-label="Close pre-check" tabIndex={-1} ref={outcome_ref}>
            <div className={`tp-banner tp-banner-${close_result_tone(result)}`} role="status">
              {result.headline}
            </div>
            <div className="tp-na-scroll">{result.results.map(render_result_row)}</div>
            <div className="tp-modal-ft">
              {result.requires_override ? (
                <button type="button" className="tp-btn-ghost" onClick={add_override_reason}>
                  Add an override reason
                </button>
              ) : null}
              <button type="button" className="tp-btn" onClick={close}>
                Done
              </button>
            </div>
          </section>
        ) : (
          <div>
            <div className="tp-ctx">
              {list_ready ? `${list.readiness.headline}. ` : ''}Closing a teacher-month writes its
              Free Pass grant and frees the later months waiting on it. The teachers are closed one
              by one on the server; you can close this dialog and follow the progress on the page.{' '}
              {CARRY_FORWARD_TEXT} An open error can never be closed over — fix it, or resolve it
              with a reason, and let the month recompute.
            </div>

            <FilterBar label="Teacher filters">
              <LabeledSegmented
                label="Teachers"
                options={STATE_OPTIONS}
                value={state}
                onChange={(v) => {
                  setPicked({});
                  setState(v || DEFAULT_READINESS_STATE);
                }}
              />
              <div className="tp-fsearch">
                <TeacherSearch
                  value={query}
                  on_change={setQuery}
                  error={search_error}
                  input_ref={search_ref}
                />
              </div>
            </FilterBar>
            <SelectionBar
              locked={!!confirm}
              shown_count={shown_selectable.length}
              shown_picked={shown_picked}
              picked_count={selected.length}
              matching={matching}
              match_all={match_all}
              noun="teacher-months"
              on_toggle_shown={toggle_shown}
              on_match_all={() => setMatchAll({ state, q: listed_query, count: list.total_count })}
              on_clear={() => {
                setPicked({});
                setMatchAll(null);
              }}
            />

            {loading ? <CustomLoading /> : null}
            {!loading && failed === 'list' ? (
              <LoadFailed text={READINESS_LIST_LOAD_FAILED_TEXT} on_retry={retry_list} />
            ) : null}
            {list_ready ? (
              <div>
                <div className="tp-na-scroll">{list.rows.map(render_row)}</div>
                {list.rows.length ? null : (
                  <div className="tp-empty">
                    {listed_query
                      ? `No teacher-months in this state match "${listed_query}".`
                      : `No teacher-months in this state for ${month_label}.`}
                  </div>
                )}
                <ReadinessPager
                  list={list}
                  loading_more={loading_more}
                  more_failed={failed === 'more'}
                  on_more={load_more}
                  on_retry={retry}
                />
              </div>
            ) : null}

            <div className="tp-field">
              <label htmlFor="tp-close-reason">
                Reason (required, recorded in the audit trail)
              </label>
              <textarea
                id="tp-close-reason"
                className="tp-textarea"
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  setReasonServerError('');
                }}
                onBlur={() => setTouched(true)}
                placeholder="Why is this month being closed now?"
              />
            </div>

            {requires_override ? (
              <div className="tp-field">
                <label htmlFor="tp-close-override">
                  Override reason (required — some selected teachers still have open must-review
                  issues)
                </label>
                <textarea
                  id="tp-close-override"
                  ref={override_ref}
                  className="tp-textarea"
                  value={override_reason}
                  onChange={(e) => {
                    setOverrideReason(e.target.value);
                    setReasonServerError('');
                  }}
                  onBlur={() => setTouched(true)}
                  placeholder="Why are you closing with those items still open?"
                />
              </div>
            ) : null}

            {reason_server_error ? (
              <div className="tp-neg" role="alert">
                {reason_server_error}
              </div>
            ) : null}
            {touched && form_error ? (
              <div className="tp-neg" role="alert">
                {form_error}
              </div>
            ) : null}
            <CreateNotice
              notice={create.notice}
              uncertain={create.uncertain}
              on_retry={create.retry}
              refusal={refusal}
              on_show_run={(run_uuid) => {
                close();
                on_show_run(run_uuid);
              }}
            />

            {confirm ? (
              <RunConfirm
                text={run_confirm_text({
                  kind: RUN_KIND_CLOSE,
                  count,
                  month_label,
                  state: match_all ? match_all.state : state
                })}
                note={confirm.note}
                confirm_label={`Close ${teacher_months(count)}`}
                busy={create.busy}
                on_confirm={confirm_send}
                on_back={() => setConfirm(null)}
              />
            ) : (
              <div className="tp-modal-ft">
                {/* aria-disabled, not disabled: disabling the focused button drops focus. */}
                <button
                  type="button"
                  className="tp-btn-ghost"
                  onClick={create.busy ? undefined : close}
                  aria-disabled={create.busy ? 'true' : undefined}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="tp-btn"
                  onClick={submit_off ? undefined : submit}
                  aria-disabled={submit_off ? 'true' : undefined}
                >
                  {create.busy
                    ? 'Starting…'
                    : count
                      ? `Close ${teacher_months(count)}`
                      : 'Close selected'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </TpDialog>
  );
};

export default CloseMonthModal;
